/**
 * Scan Bills Step 1 — choose accounts and date range to scan
 */

import React from 'react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { AccountFilterDropdown } from '../../../components/transactions/AccountFilterDropdown';
import {
  TransactionDatePicker,
  type DateRange,
} from '../../../components/transactions/TransactionDatePicker';

export function getDefaultScanDateRange(): DateRange {
  const now = new Date();
  const from = startOfMonth(subMonths(now, 6));
  const to = endOfMonth(subMonths(now, 0)) > now ? now : endOfMonth(now);
  return {
    from: format(from, 'yyyy-MM-dd'),
    to: format(to > now ? now : to, 'yyyy-MM-dd'),
  };
}

interface ScanBillsStep1OptionsProps {
  accountIds: string[];
  onAccountIdsChange: (ids: string[]) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  onScan: () => void;
  scanning: boolean;
  error?: string | null;
}

export function ScanBillsStep1Options({
  accountIds,
  onAccountIdsChange,
  dateRange,
  onDateRangeChange,
  onScan,
  scanning,
  error,
}: ScanBillsStep1OptionsProps): React.ReactElement {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Choose what to scan
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Select accounts and a date range. Leave accounts empty to scan all accounts.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Date range
          </label>
          <TransactionDatePicker value={dateRange} onChange={onDateRangeChange} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Accounts
          </label>
          <AccountFilterDropdown
            value={undefined}
            valueIds={accountIds}
            onChange={() => {}}
            onChangeIds={onAccountIdsChange}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            {accountIds.length === 0
              ? 'Scanning all accounts'
              : `${accountIds.length} account${accountIds.length === 1 ? '' : 's'} selected`}
          </p>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onScan}
          disabled={scanning || !dateRange.from || !dateRange.to}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
        >
          {scanning ? 'Scanning…' : 'Scan transactions'}
        </button>
      </div>
    </div>
  );
}
